import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  ThemeOptions,
  ThemeOverride,
} from '@wix/da-react-context/pkg/ThemeContext';
import { formatDate, DATE_FORMATS } from '@wix/da-i18next/pkg/time/functions';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import TrashIcon from '@wix/da-ds/pkg/Icons/24x24/Trashcan';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { scheduleDraftPublish } from '../../../../redux/actions';

import s from './ScheduledPublishBanner.scss';

interface Props {
  deviation: PapiDeviation;
  timestamp: string | number;
  disableButtons?: boolean;
}

const ScheduledPublishBanner: React.FC<Props> = ({
  deviation,
  timestamp,
  disableButtons,
}) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const disableSchedule = useCallback(() => {
    dispatch(
      scheduleDraftPublish({
        deviation,
        scheduleTs: 0,
      })
    );
  }, [dispatch, deviation]);

  const editSchedule = useCallback(() => {
    dispatch(
      pushModal(ModalType.DEVIATION_SUBMIT_SCHEDULE, {
        params: {
          deviation,
          scheduleTimestamp: deviation.scheduledPublishTime,
        },
      })
    );
  }, [dispatch, deviation]);

  return (
    <div className={s['root']}>
      <span className={s['text']}>
        {t('deviationSubmit.form.scheduledBanner.text', {
          postProcess: 'reactPostprocessor',
          scheduledPublishTime: formatDate(
            timestamp,
            DATE_FORMATS.DateTime.Full,
            i18n.language
          ),
        })}
      </span>
      <ThemeOverride theme={ThemeOptions.DARK}>
        <span className={s['buttons']}>
          <TextButton
            size="small"
            variant="main"
            icon={TrashIcon}
            iconProps={{
              size: IconSize.SMALLER,
              disableDefaultColors: true,
            }}
            onClick={disableSchedule}
            disabled={disableButtons}
          >
            {t('deviationSubmit.form.scheduledBanner.button.disable')}
          </TextButton>
          <TextButton
            size="small"
            variant="main"
            icon={EditIcon}
            iconProps={{
              size: IconSize.SMALLER,
              disableDefaultColors: true,
            }}
            onClick={editSchedule}
            disabled={disableButtons}
          >
            {t('deviationSubmit.form.scheduledBanner.button.edit')}
          </TextButton>
        </span>
      </ThemeOverride>
    </div>
  );
};
ScheduledPublishBanner.displayName = 'ScheduledPublishBanner';

export default ScheduledPublishBanner;
